import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Typography,
  Alert,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  LibraryBooks
} from '@mui/icons-material';
import Layout from '../components/Layout';
import SubjectVersionFilter from '../components/SubjectVersionFilter';
import { useAuth } from '../contexts/AuthContext';
import { curriculumAPI } from '../services/api';

// 类型定义
interface Subject {
  id: number;
  name: string;
  description?: string;
}

interface SubjectVersion {
  id: number;
  name: string;
  description?: string;
  schoolLevel: string;
  gradeKey?: string;
  stageCode?: string;
  termKey?: string;
  orderIndex?: number;
  subjectId: number;
  subjectName: string;
  createdAt: string;
  updatedAt: string;
}

interface SubjectVersionForm {
  name: string;
  description: string;
  schoolLevel: string;
  gradeKey?: string;
  stageCode?: string;
  termKey?: string;
  subjectId: number;
  orderIndex?: number | null;
}

const SubjectVersionManagePage: React.FC = () => {
  const { isSuperAdmin } = useAuth();
  const [subjectVersions, setSubjectVersions] = useState<SubjectVersion[]>([]);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [filterSubjectId, setFilterSubjectId] = useState<number | ''>('');

  // 对话框状态
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [versionToDelete, setVersionToDelete] = useState<SubjectVersion | null>(null);
  const [versionToEdit, setVersionToEdit] = useState<SubjectVersion | null>(null);

  // 表单状态
  const [form, setForm] = useState<SubjectVersionForm>({
    name: '',
    description: '',
    schoolLevel: '',
    gradeKey: '',
    stageCode: '',
    termKey: '',
    subjectId: 0,
    orderIndex: undefined
  });

  const stageOptions = [
    { value: 'PRIMARY', label: '小学' },
    { value: 'MIDDLE', label: '初中' },
    { value: 'HIGH', label: '高中' }
  ];

  const gradeOptions = [
    '一年级', '二年级', '三年级', '四年级', '五年级', '六年级',
    '七年级', '八年级', '九年级',
    '高一', '高二', '高三'
  ];

  const termOptions = [
    { value: '上', label: '上' },
    { value: '下', label: '下' }
  ];

  const stageLabel = (code?: string) => {
    if (!code) return '-';
    const normalized = code.toUpperCase();
    if (normalized === 'PRIMARY') return '小学';
    if (normalized === 'MIDDLE') return '初中';
    if (normalized === 'HIGH') return '高中';
    return code;
  };

  // 常用年级水平选项（用于快速选择）
  const commonSchoolLevels = [
    '小学一年级上', '小学一年级下', '小学二年级上', '小学二年级下',
    '小学三年级上', '小学三年级下', '小学四年级上', '小学四年级下',
    '小学五年级上', '小学五年级下', '小学六年级上', '小学六年级下',
    '七年级上', '七年级下', '八年级上', '八年级下', '九年级上', '九年级下',
    '高一上', '高一下', '高二上', '高二下', '高三上', '高三下'
  ];

  // 快速选择年级水平
  const handleQuickSelect = (schoolLevel: string) => {
    setForm(prev => ({ ...prev, schoolLevel }));
  };

  // 加载数据
  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [versionsData, subjectsData] = await Promise.all([
        curriculumAPI.getAllSubjectVersions(),
        curriculumAPI.getSubjects()
      ]);
      setSubjectVersions(versionsData);
      setSubjects(subjectsData);
    } catch (err) {
      setError('加载数据失败');
      console.error('加载数据失败:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const filteredSubjectVersions = useMemo(() => {
    let versions = filterSubjectId
      ? subjectVersions.filter(v => v.subjectId === filterSubjectId)
      : subjectVersions;

    // 排序：先按学科ID，再按排序号（null/undefined排最后），最后按创建时间
    return versions.sort((a, b) => {
      // 1. 按学科ID排序
      if (a.subjectId !== b.subjectId) {
        return a.subjectId - b.subjectId;
      }

      // 2. 按orderIndex排序（null/undefined排最后）
      const aOrder = a.orderIndex ?? null;
      const bOrder = b.orderIndex ?? null;

      if (aOrder === null && bOrder === null) return 0;
      if (aOrder === null) return 1;
      if (bOrder === null) return -1;
      if (aOrder !== bOrder) {
        return aOrder - bOrder;
      }

      // 3. 按创建时间排序
      return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    });
  }, [subjectVersions, filterSubjectId]);

  // 处理表单输入
  const handleInputChange = (field: keyof SubjectVersionForm, value: string | number | undefined | null) => {
    setForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // 处理下拉选择
  const handleSelectChange = (event: SelectChangeEvent<string | number>) => {
    const { name, value } = event.target;
    handleInputChange(name as keyof SubjectVersionForm, value);
  };

  // 打开新增对话框
  const handleAdd = () => {
    setForm({
      name: '',
      description: '',
      schoolLevel: '',
      gradeKey: '',
      stageCode: '',
      termKey: '',
      subjectId: 0,
      orderIndex: null
    });
    setDialogOpen(true);
  };

  // 打开编辑对话框
  const handleEdit = (version: SubjectVersion) => {
    setVersionToEdit(version);
    setForm({
      name: version.name,
      description: version.description || '',
      schoolLevel: version.schoolLevel,
      gradeKey: version.gradeKey || '',
      stageCode: version.stageCode || '',
      termKey: version.termKey || '',
      subjectId: version.subjectId,
      orderIndex: version.orderIndex
    });
    setEditDialogOpen(true);
  };

  // 保存学科版本（用于新增）
  const handleSave = async () => {
    if (!form.name.trim() || !form.schoolLevel.trim() || !form.subjectId) {
      setError('请填写必填字段（版本名称、年级水平和所属学科）');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      console.log('createSubjectVersion payload:', form);
      await curriculumAPI.createSubjectVersion(form);
      setSuccess('学科版本创建成功');
      setDialogOpen(false);
      await loadData();
    } catch (err) {
      setError('创建学科版本失败');
      console.error('保存学科版本失败:', err);
    } finally {
      setLoading(false);
    }
  };

  // 保存编辑的学科版本
  const handleEditSave = async () => {
    if (!versionToEdit) return;

    if (!form.name.trim() || !form.schoolLevel.trim()) {
      setError('请填写必填字段（版本名称和年级水平）');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      console.log("updateSubjectVersion payload:", versionToEdit?.id, { name: form.name, description: form.description, schoolLevel: form.schoolLevel, subjectId: versionToEdit?.subjectId, orderIndex: form.orderIndex });
      // 编辑时允许修改 name、description 和 schoolLevel，但不允许修改 subjectId
      const formDataToSave = {
        name: form.name,
        description: form.description,
        schoolLevel: form.schoolLevel,
        stageCode: form.stageCode || undefined,
        gradeKey: form.gradeKey || undefined,
        termKey: form.termKey || undefined,
        subjectId: versionToEdit.subjectId,
        orderIndex: form.orderIndex
      };

      await curriculumAPI.updateSubjectVersion(versionToEdit.id, formDataToSave);
      setSuccess('学科版本更新成功');
      setEditDialogOpen(false);
      setVersionToEdit(null);
      await loadData();
    } catch (err) {
      setError('更新学科版本失败');
      console.error('更新学科版本失败:', err);
    } finally {
      setLoading(false);
    }
  };

  // 确认删除
  const handleDeleteConfirm = (version: SubjectVersion) => {
    setVersionToDelete(version);
    setDeleteDialogOpen(true);
  };

  // 执行删除
  const handleDelete = async () => {
    if (!versionToDelete) return;

    setLoading(true);
    setError(null);
    try {
      const response = await curriculumAPI.deleteSubjectVersion(versionToDelete.id);
      if (response.deletedFilesCount > 0) {
        setSuccess(`学科版本删除成功，已同时删除 ${response.deletedFilesCount} 个文件`);
      } else {
        setSuccess('学科版本删除成功');
      }
      setDeleteDialogOpen(false);
      setVersionToDelete(null);
      await loadData();
    } catch (err) {
      setError('删除学科版本失败');
      console.error('删除学科版本失败:', err);
    } finally {
      setLoading(false);
    }
  };

  // 获取学科名称
  const getSubjectName = (subjectId: number) => {
    const subject = subjects.find(s => s.id === subjectId);
    return subject?.name || '未知学科';
  };

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <LibraryBooks sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h4" fontWeight="bold">
              学科版本管理
            </Typography>
          </Box>
        <Box>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadData}
            sx={{ mr: 2 }}
            disabled={loading}
          >
            刷新
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAdd}
            disabled={loading}
          >
            新增学科版本
          </Button>
        </Box>
      </Box>

      {/* 学科筛选 */}
      <Box sx={{ mb: 2 }}>
        <SubjectVersionFilter
          subjects={subjects}
          subjectVersions={[]}
          selectedSubjectId={filterSubjectId || ''}
          onSubjectChange={(sid) => setFilterSubjectId((sid as number) || '')}
          showVersions={false}
          size="small"
          sx={{ maxWidth: 360 }}
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>版本名称</TableCell>
              <TableCell>所属学科</TableCell>
              <TableCell>年级水平</TableCell>
              <TableCell>阶段</TableCell>
              <TableCell>年级</TableCell>
              <TableCell>上下</TableCell>
              <TableCell>排序序号</TableCell>
              <TableCell>描述</TableCell>
              <TableCell>创建时间</TableCell>
              <TableCell>操作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredSubjectVersions.map((version, idx) => (
              <TableRow key={version.id}>
                <TableCell>{version.id}</TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight="medium">
                    {version.name}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip 
                    label={version.subjectName} 
                    size="small" 
                    color="primary" 
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <Chip 
                    label={version.schoolLevel} 
                    size="small" 
                    color="secondary"
                  />
                </TableCell>
                <TableCell>{stageLabel(version.stageCode)}</TableCell>
                <TableCell>{version.gradeKey || '-'}</TableCell>
                <TableCell>{version.termKey || '-'}</TableCell>
                <TableCell>{version.orderIndex ?? '-'}</TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {version.description || '-'}
                  </Typography>
                </TableCell>
                <TableCell>
                  {new Date(version.createdAt).toLocaleDateString()}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <IconButton
                      size="small"
                      onClick={() => handleEdit(version)}
                      disabled={loading}
                      color="primary"
                      title="编辑学科版本"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteConfirm(version)}
                      disabled={loading || !isSuperAdmin}
                      color="error"
                      title={!isSuperAdmin ? "只有超级管理员可以删除学科版本" : "删除学科版本"}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            {filteredSubjectVersions.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={11} align="center">
                  <Typography color="text.secondary">
                    暂无学科版本数据
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* 新增对话框 */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          新增学科版本
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>所属学科 *</InputLabel>
              <Select
                name="subjectId"
                value={form.subjectId || ''}
                onChange={handleSelectChange}
                label="所属学科 *"
              >
                {subjects.map((subject) => (
                  <MenuItem key={subject.id} value={subject.id}>
                    {subject.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="版本名称"
              value={form.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              sx={{ mb: 2 }}
              required
            />

            {/* 年级水平输入 */}
            <TextField
              fullWidth
              label="年级水平"
              value={form.schoolLevel}
              onChange={(e) => handleInputChange('schoolLevel', e.target.value)}
              sx={{ mb: 2 }}
              required
              placeholder="例如：小学一年级上、九年级下、高二上等"
              helperText="请输入年级水平，例如：小学一年级上、九年级下、高二上等"
            />

            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <FormControl fullWidth>
                <InputLabel>阶段</InputLabel>
                <Select
                  name="stageCode"
                  value={form.stageCode || ''}
                  onChange={handleSelectChange}
                  label="阶段"
                >
                  <MenuItem value="">（自动识别）</MenuItem>
                  {stageOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>年级</InputLabel>
                <Select
                  name="gradeKey"
                  value={form.gradeKey || ''}
                  onChange={handleSelectChange}
                  label="年级"
                >
                  <MenuItem value="">（自动识别）</MenuItem>
                  {gradeOptions.map((g) => (
                    <MenuItem key={g} value={g}>{g}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>上下</InputLabel>
                <Select
                  name="termKey"
                  value={form.termKey || ''}
                  onChange={handleSelectChange}
                  label="上下"
                >
                  <MenuItem value="">（自动识别）</MenuItem>
                  {termOptions.map((t) => (
                    <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* 常用选项快速选择 */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                常用选项快速选择：
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {commonSchoolLevels.map((level) => (
                  <Button
                    key={level}
                    size="small"
                    variant="outlined"
                    onClick={() => handleQuickSelect(level)}
                    sx={{ fontSize: '0.75rem', minWidth: 'auto', px: 1 }}
                  >
                    {level}
                  </Button>
                ))}
              </Box>
            </Box>

            <TextField
              fullWidth
              label="排序序号"
              type="number"
              value={form.orderIndex ?? ""}
              onChange={(e) => handleInputChange('orderIndex', e.target.value === '' ? null : Number(e.target.value))}
              sx={{ mb: 2 }}
              helperText="留空则自动追加到该学科最后"
              inputProps={{ min: 1 }}
            />
            <TextField
              fullWidth
              label="备注"
              value={form.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              multiline
              rows={3}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>取消</Button>
          <Button onClick={handleSave} variant="contained" disabled={loading}>
            创建
          </Button>
        </DialogActions>
      </Dialog>

      {/* 删除确认对话框 */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>确认删除</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight="bold">
              警告：级联删除操作
            </Typography>
          </Alert>
          <Typography>
            确定要删除学科版本 "{versionToDelete?.name}" 吗？
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            此操作将同时删除：
          </Typography>
          <Typography variant="body2" color="text.secondary" component="ul" sx={{ mt: 1, pl: 2 }}>
            <li>该版本下的所有章节</li>
            <li>所有章节下的知识点</li>
            <li>知识点关联的视频和图片文件</li>
          </Typography>
          <Typography variant="body2" color="error" sx={{ mt: 1, fontWeight: 'bold' }}>
            此操作不可撤销，请谨慎操作！
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>取消</Button>
          <Button onClick={handleDelete} color="error" disabled={loading}>
            删除
          </Button>
        </DialogActions>
      </Dialog>

      {/* 编辑对话框 */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          编辑学科版本
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {/* 所属学科（只读） */}
            <FormControl fullWidth sx={{ mb: 2 }} disabled>
              <InputLabel>所属学科</InputLabel>
              <Select
                value={form.subjectId || ''}
                label="所属学科"
              >
                {subjects.map((subject) => (
                  <MenuItem key={subject.id} value={subject.id}>
                    {subject.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* 版本名称 */}
            <TextField
              fullWidth
              label="版本名称"
              value={form.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              sx={{ mb: 2 }}
              required
            />

            {/* 年级水平输入 */}
            <TextField
              fullWidth
              label="年级水平"
              value={form.schoolLevel}
              onChange={(e) => handleInputChange('schoolLevel', e.target.value)}
              sx={{ mb: 2 }}
              required
              placeholder="例如：小学一年级上、九年级下、高二上等"
              helperText="请输入年级水平，例如：小学一年级上、九年级下、高二上等"
            />

            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <FormControl fullWidth>
                <InputLabel>阶段</InputLabel>
                <Select
                  name="stageCode"
                  value={form.stageCode || ''}
                  onChange={handleSelectChange}
                  label="阶段"
                >
                  <MenuItem value="">（自动识别）</MenuItem>
                  {stageOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>年级</InputLabel>
                <Select
                  name="gradeKey"
                  value={form.gradeKey || ''}
                  onChange={handleSelectChange}
                  label="年级"
                >
                  <MenuItem value="">（自动识别）</MenuItem>
                  {gradeOptions.map((g) => (
                    <MenuItem key={g} value={g}>{g}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>上下</InputLabel>
                <Select
                  name="termKey"
                  value={form.termKey || ''}
                  onChange={handleSelectChange}
                  label="上下"
                >
                  <MenuItem value="">（自动识别）</MenuItem>
                  {termOptions.map((t) => (
                    <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <TextField
              fullWidth
              label="排序序号"
              type="number"
              value={form.orderIndex ?? ''}
              onChange={(e) => handleInputChange('orderIndex', e.target.value === '' ? null : Number(e.target.value))}
              sx={{ mb: 2 }}
              inputProps={{ min: 1 }}
            />

            {/* 常用选项快速选择 */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                常用选项快速选择：
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {commonSchoolLevels.map((level) => (
                  <Button
                    key={level}
                    size="small"
                    variant="outlined"
                    onClick={() => handleQuickSelect(level)}
                    sx={{ fontSize: '0.75rem', minWidth: 'auto', px: 1 }}
                  >
                    {level}
                  </Button>
                ))}
              </Box>
            </Box>

            {/* 描述 */}
            <TextField
              fullWidth
              label="描述"
              value={form.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              multiline
              rows={3}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>取消</Button>
          <Button onClick={handleEditSave} variant="contained" disabled={loading}>
            保存
          </Button>
        </DialogActions>
      </Dialog>
      </Box>
    </Layout>
  );
};

export default SubjectVersionManagePage;





